import { Component, OnInit, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { OperationalChargesService } from '../../../../services/forwarding/operational-charges';
import { OperationalCharge, OperationalChargeStats, ChargeMode } from '../../../../models/forwarding/operational-charge.model';
import { ChargeBasisService } from '../../../../services/forwarding/charge-basis.service';
import { ChargeBasis } from '../../../../models/forwarding/charge-basis.model';

@Component({
  selector: 'app-operational-charges-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './operational-charges-modal.html',
  styleUrl: './operational-charges-modal.css'
})
export class OperationalChargesModalComponent implements OnInit, OnDestroy {
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<void>();

  charges: OperationalCharge[] = [];
  filteredCharges: OperationalCharge[] = [];
  chargeBasisList: ChargeBasis[] = [];
  stats: OperationalChargeStats = {
    totalCharges: 0,
    mandatoryCharges: 0,
    optionalCharges: 0,
    averagePrice: 0
  };

  // Filter properties
  searchTerm = '';
  selectedMode = '';
  selectedChargeType = '';

  // Form properties
  showForm = false;
  isEditMode = false;
  editingChargeId: number | null = null;
  formData: Partial<OperationalCharge> = this.getEmptyFormData();

  private subscriptions = new Subscription();

  constructor(
    private operationalChargesService: OperationalChargesService,
    private chargeBasisService: ChargeBasisService
  ) {}

  ngOnInit(): void {
    this.loadCharges();
    this.loadChargeBasis();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadCharges(): void {
    this.subscriptions.add(
      this.operationalChargesService.getCharges().subscribe(charges => {
        this.charges = charges;
        this.applyFilters();
        this.updateStats();
      })
    );
  }

  loadChargeBasis(): void {
    // Subscribe to charge basis changes - automatically updates when charge basis is added/edited
    this.subscriptions.add(
      this.chargeBasisService.getCharges().subscribe(basisList => {
        this.chargeBasisList = basisList;
        console.log('[OperationalChargesModal] Charge basis updated:', basisList.length, 'units');
      })
    );
  }

  updateStats(): void {
    this.stats = this.operationalChargesService.getStats();
  }

  applyFilters(): void {
    this.filteredCharges = this.operationalChargesService.filterCharges(
      this.searchTerm,
      this.selectedMode,
      this.selectedChargeType
    );
  }

  getModeDisplay(mode: ChargeMode): string {
    const modeMap: Record<ChargeMode, string> = {
      'SEA_LCL': 'SEA (LCL)',
      'SEA_FCL': 'SEA (FCL)',
      'SEA_ALL': 'SEA (ALL)',
      'AIR': 'AIR',
      'ALL': 'ALL'
    };
    return modeMap[mode] || mode;
  }

  getChargeBasisNotes(displayName: string): string | undefined {
    return this.chargeBasisList.find(b => b.displayName === displayName)?.notes;
  }

  openAddForm(): void {
    this.isEditMode = false;
    this.editingChargeId = null;
    this.formData = this.getEmptyFormData();
    this.showForm = true;
  }

  editCharge(charge: OperationalCharge): void {
    this.isEditMode = true;
    this.editingChargeId = charge.id;
    this.formData = { ...charge };
    this.showForm = true;
  }

  deleteCharge(charge: OperationalCharge): void {
    if (confirm(`Are you sure you want to delete "${charge.feeName}"?`)) {
      this.operationalChargesService.deleteCharge(charge.id);
      this.loadCharges();
    }
  }

  saveCharge(): void {
    if (this.isEditMode && this.editingChargeId) {
      this.operationalChargesService.updateCharge(this.editingChargeId, this.formData);
    } else {
      this.operationalChargesService.addCharge({
        ...this.formData as Omit<OperationalCharge, 'id' | 'createdAt'>,
        isActive: true
      });
    }
    this.closeForm();
    this.loadCharges();
  }

  closeForm(): void {
    this.showForm = false;
    this.formData = this.getEmptyFormData();
  }

  close(): void {
    this.closeModal.emit();
  }

  private getEmptyFormData(): Partial<OperationalCharge> {
    return {
      feeName: '',
      mode: 'ALL',
      chargeCode: 'OF',
      chargeBasis: '',
      defaultPrice: 0,
      currency: 'PHP',
      chargeType: 'Flat Rate',
      isOptional: false,
      portApplication: 'Origin',
      description: '',
      isAgentSpecific: false,
      isTaxable: false,
      isActive: true
    };
  }
}